import React from 'react'
import Link from 'next/link'
import * as EmailValidator from 'email-validator'
import {isValidNumber} from 'libphonenumber-js'
import HeadTitle from '../components/head_title'
import * as PasswordUtils from '../utils/password'
import Api from '../service/api'
import {InputFieldWithValidation, ValidationFeedback,} from '../components/fields_with_validation'
import Layout from '../components/layout/layout'
import Breadcrumbs from '../components/breadcrumbs'
import TimeoutAlert from '../components/timeout_alert'
import styles from '../styles/sign_up.module.scss'
import {extractAuthError} from '../utils/error'
import {genericCheckboxChange, genericTextChange, wrapFieldErrors} from '../utils/forms'
import countriesAndStates, {ascCountryCodes} from "../constants/address_fields";
import {serializeDate} from "../utils/date";
import moment from "moment";
import {isBlank} from "../utils/string";
import {UserProfile} from "../types/models/user";
import CustomPhoneInput from "../components/phone_input/custom_input";

type FieldErrors = Partial<Record<
  'email' |
  'password' |
  'confirmPassword' |
  'firstName' |
  'lastName' |
  'phoneNumber' |
  'agree', string[]>
  >;

export default function SignUp() {
  const [completed, setCompleted] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [birthDate, setBirthdate] = React.useState('');
  const [gender, setGender] = React.useState<string>('');
  const [agree, setAgree] = React.useState(false);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [serverError, setServerError] = React.useState<string | null>(null);

  const validateFields = React.useCallback(() => {
    const errors: Required<FieldErrors> = {
      email: [],
      password: [],
      confirmPassword: [],
      firstName: [],
      lastName: [],
      phoneNumber: [],
      agree: [],
    };
    // validate fields
    if (email.length === 0) {
      errors.email.push('Email is required');
    } else {
      if (!EmailValidator.validate(email)) {
        errors.email.push('Email is invalid');
      }
    }

    if (password.length === 0) {
      errors.password.push('Password is required');
    } else {
      const errorMessage = PasswordUtils.validate(password);
      if (errorMessage) {
        errors.password.push('Password is invalid');
        errors.password.push(errorMessage);
      }
    }

    if (confirmPassword.length === 0) {
      errors.confirmPassword.push('Confirm password is required');
    } else {
      if (confirmPassword !== password) {
        errors.confirmPassword.push('Confirm password is different from entered password');
      }
    }

    if (firstName.length === 0) {
      errors.firstName.push('First name is required');
    }

    if (lastName.length === 0) {
      errors.lastName.push('Last name is required');
    }

    if (phoneNumber.length === 0) {
      errors.phoneNumber.push('Phone number is required');
    } else {
      if (!isValidNumber(phoneNumber)) {
        errors.phoneNumber.push('Invalid phone number');
      }
    }

    if (!agree) {
      errors.agree.push('Please accept the terms and condition');
    }

    setErrors(errors);
    const isInvalid = Object.values(errors).some(value => value.length > 0);
    return !isInvalid;
  }, [email, password, confirmPassword, firstName, lastName, phoneNumber, agree]);

  const handleSignUp = React.useCallback(() => {
    const fieldsValid = validateFields();
    if (!fieldsValid) {
      return;
    }

    const serializedBirthdate = isBlank(birthDate) ? null : serializeDate(moment(birthDate).toDate());
    const finalCountry = isBlank(country) ? null : country;
    const finalGender = isBlank(gender) ? null : gender;

    Api.auth.register(
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      finalCountry,
      serializedBirthdate,
      finalGender as UserProfile['gender'],
      agree
    )
    .then(() => {
      setCompleted(true);
    })
    .catch((error) => {
      const errorMessage = extractAuthError(error);
      setServerError(errorMessage);
    })
  }, [validateFields, email, password, firstName, lastName, phoneNumber, agree, birthDate, country, gender]);

  const emailChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'email', genericTextChange(setEmail)),
    [errors]);
  const passwordChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'password', genericTextChange(setPassword)),
    [errors]);
  const confirmPasswordChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'confirmPassword', genericTextChange(setConfirmPassword)),
    [errors]);
  const firstNameChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'firstName', genericTextChange(setFirstName)),
    [errors]);
  const lastNameChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'lastName', genericTextChange(setLastName)),
    [errors]);
  const phoneNumberChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'phoneNumber', setPhoneNumber),
    [errors]);
  const agreeChangeHandler = React.useCallback(
    wrapFieldErrors(errors, setErrors, 'agree', genericCheckboxChange(setAgree)),
    [errors]);

  let content
  if (!completed) {
  	content = (
  		<React.Fragment>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <InputFieldWithValidation type="text"
            className="form-control"
            value={email}
            errors={errors.email}
            onChange={emailChangeHandler}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Password *</label>
          <InputFieldWithValidation type="password"
            className="form-control"
            value={password}
            errors={errors.password}
            onChange={passwordChangeHandler}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Confirm Password *</label>
          <InputFieldWithValidation type="password"
            className="form-control"
            value={confirmPassword}
            errors={errors.confirmPassword}
            onChange={confirmPasswordChangeHandler}
          />
        </div>
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <InputFieldWithValidation type="text"
            className="form-control"
            value={firstName}
            errors={errors.firstName}
            onChange={firstNameChangeHandler}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name *</label>
          <InputFieldWithValidation type="text"
            className="form-control"
            value={lastName}
            errors={errors.lastName}
            onChange={lastNameChangeHandler}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Phone Number *</label>
          <CustomPhoneInput
            value={phoneNumber}
            onChange={phoneNumberChangeHandler}
          />
          <ValidationFeedback
            errors={errors.phoneNumber}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Country</label>
            <select value={country} onChange={genericTextChange(setCountry)} className="form-control">
              <option value="">Prefer Not To Say</option>
              {ascCountryCodes.map((countryCode, index) => (
                <option value={countryCode} key={index}>{countriesAndStates.countries[countryCode].displayValue}</option>
              ))}
            </select>
        </div>

        <div className="form-group">
          <label className="form-label">Birthdate</label>
          <InputFieldWithValidation
            type="date"
            className="form-control"
            value={birthDate}
            onChange={(e) => setBirthdate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Gender</label>
          <select value={gender} onChange={genericTextChange(setGender)} className="form-control">
            <option value="">Prefer Not To Say</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
          </select>
        </div>

        <div className="form-group">
          <p>* Required Fields</p>
        </div>

        <div className="form-check mb-3">
          <InputFieldWithValidation
            className="form-check-input"
            type="checkbox"
            id="user-agree-terms"
            checked={agree}
            onChange={agreeChangeHandler}
            errors={errors.agree}
          />
          <label className="form-check-label" htmlFor="user-agree-terms">
            Accept to our
            <Link href="/pages/terms_and_conditions" rel="noopener noreferrer" target="_blank">

              {` `}terms and conditions
                            
            </Link>
          </label>
        </div>

        <button className="btn btn-primary text-uppercase" onClick={handleSignUp}>Sign Up</button>

  		</React.Fragment>
  	);
  } else {
    content = <React.Fragment>
      <p>Thank you for registering. Please verify your email address before proceeding to login.</p>
      <Link href="/sign_in" className="btn btn-primary">Go to Login page</Link>
    </React.Fragment>;
  }

  return <Layout>
    <HeadTitle title="Sign Up" />
    <Breadcrumbs
      currentPage="Sign Up"
    />
    <TimeoutAlert
      message={serverError}
      onHide={() => setServerError(null)}
    />

    <div className="row pb-4">
      <div className="col-0 col-sm-5">
        <div className="pb-2">
          <div className={styles.illustration}>
            Our Main
            <br/>
            <span className="ml-2">Feature</span>
          </div>
          <img src="/images/signup-top-illustration.png" alt="illustration" />
        </div>

        <div className="card bg-dark mb-2">
          <div className="card-body text-white font-weight-semibold">
            Can’t find what you want on our product list?
          </div>
        </div>

        <div className="card bg-dark mb-2">
          <div className="card-body text-white font-weight-semibold">
             Copy and Paste your product links from 3rd party marketplace
          </div>
        </div>

        <div className="card bg-dark mb-2">
          <div className="card-body text-white font-weight-semibold">
            We will provide you with the quotation of your desired product
          </div>
        </div>

        <div className="card bg-dark mb-2">
          <div className="card-body text-white font-weight-semibold">
            Purchase your products and we will deliver it to you right to your doorstep!
          </div>
        </div>

        <div>
          <div className={styles.illustration}>
            Sign Up
            <br/>
            <span className="ml-2">With Us Now!</span>
          </div>
          <img src="/images/signup-bottom-illustration.png" alt="illustration" />
        </div>
      </div>
      <div className="col-sm-7">
        <h2 className="text-primary">Sign Up</h2>
        <hr/>
        {content}
      </div>
    </div>
  </Layout>;
}

export { basicGetStaticProps as getStaticProps } from '../lib/static_props'